import { useEffect, useState, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'
import { apiClient } from '@/services/api'
import {
  ArrowRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CpuChipIcon,
  CloudIcon,
  DocumentMagnifyingGlassIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  FingerPrintIcon,
} from '@heroicons/react/24/outline'
import type { AgentGraphResponse, GraphNodeStatus } from '@/types'

// Inline type for crew graph since API response may differ slightly
interface CrewGraphApiResponse {
  crew_name: string
  display_name: string
  version: string
  process_type: 'sequential' | 'parallel' | 'hierarchical'
  nodes: Array<{
    id: string
    type: string
    label: string
    description: string
    status: string
    position: { x: number; y: number }
    metrics?: { tokens: number; duration_ms: number; cost: number }
  }>
  edges: Array<{
    id: string
    source: string
    target: string
    condition?: string
  }>
  metadata: {
    domain: string
    agent_count: number
    agents: Array<{ name: string; role: string }>
  }
}

type GraphData = AgentGraphResponse | CrewGraphApiResponse

interface LangGraphVisualizerProps {
  agentName?: string
  crewName?: string
  /** Reserved for future use with real-time execution tracking */
  executionId?: string
  onNodeClick?: (nodeId: string) => void
  compact?: boolean
  height?: number
}

// Suppress unused variable warning for executionId - reserved for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars

const NODE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  context: CloudIcon,
  compaction: DocumentMagnifyingGlassIcon,
  execution: CpuChipIcon,
  review: ShieldCheckIcon,
  memory: CircleStackIcon,
  audit: FingerPrintIcon,
  start: ArrowRightIcon,
  end: CheckCircleIcon,
  agent: CpuChipIcon,
  fork: ArrowRightIcon,
  join: ArrowRightIcon,
  manager: CpuChipIcon,
  worker: CpuChipIcon,
  synthesis: CpuChipIcon,
}

const STATUS_COLORS: Record<GraphNodeStatus, { bg: string; border: string; text: string }> = {
  pending: { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-600' },
  running: { bg: 'bg-blue-500/10', border: 'border-blue-500', text: 'text-blue-600' },
  completed: { bg: 'bg-green-500/10', border: 'border-green-500', text: 'text-green-600' },
  error: { bg: 'bg-red-500/10', border: 'border-red-500', text: 'text-red-600' },
  skipped: { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-400' },
}

const VALID_STATUSES = new Set<GraphNodeStatus>(['pending', 'running', 'completed', 'error', 'skipped'])

function normalizeStatus(status: string): GraphNodeStatus {
  if (VALID_STATUSES.has(status as GraphNodeStatus)) {
    return status as GraphNodeStatus
  }
  return 'pending'
}

const LangGraphVisualizer = observer(({
  agentName,
  crewName,
  executionId: _executionId, // Reserved for future real-time tracking
  onNodeClick,
  compact = false,
  height,
}: LangGraphVisualizerProps) => {
  const [graphData, setGraphData] = useState<GraphData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedNode, setSelectedNode] = useState<string | null>(null)

  const fetchGraphData = useCallback(async () => {
    if (!agentName && !crewName) {
      setError('Either agentName or crewName is required')
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      if (agentName) {
        const data = await apiClient.getAgentGraph(agentName)
        setGraphData(data)
      } else if (crewName) {
        const data = await apiClient.getCrewGraph(crewName)
        setGraphData(data)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch graph data')
    } finally {
      setIsLoading(false)
    }
  }, [agentName, crewName])

  useEffect(() => {
    fetchGraphData()
  }, [fetchGraphData])

  const handleNodeClick = (nodeId: string) => {
    setSelectedNode(nodeId === selectedNode ? null : nodeId)
    onNodeClick?.(nodeId)
  }

  const getStatusIcon = (status: GraphNodeStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />
      case 'running':
        return <ClockIcon className="w-4 h-4 text-blue-500 animate-spin" />
      case 'error':
        return <XCircleIcon className="w-4 h-4 text-red-500" />
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <Card className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="text-gray-600 mt-4 text-sm">Loading graph...</p>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="text-center py-8">
        <XCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-2" />
        <p className="text-red-600 text-sm">{error}</p>
      </Card>
    )
  }

  if (!graphData) {
    return (
      <Card className="text-center py-8">
        <p className="text-gray-600 text-sm">No graph data available</p>
      </Card>
    )
  }

  const { nodes, edges } = graphData

  // Calculate dimensions based on node positions
  const maxX = Math.max(...nodes.map(n => n.position?.x || 0)) + 200
  const maxY = Math.max(...nodes.map(n => n.position?.y || 0)) + 100
  const svgWidth = Math.max(maxX, 500)
  const svgHeight = Math.max(maxY, height || 400)

  return (
    <div className={`space-y-4 ${compact ? '' : ''}`}>
      {/* Graph Header */}
      {!compact && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {'display_name' in graphData ? graphData.display_name : 'Execution Graph'}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="primary" size="sm">
                {'process_type' in graphData ? graphData.process_type : 'langgraph'}
              </Badge>
              <span className="text-xs text-gray-500">v{graphData.version}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {Object.entries(STATUS_COLORS).map(([status, colors]) => (
              <div key={status} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${colors.bg} ${colors.border} border`} />
                <span className="text-xs text-gray-500 capitalize">{status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Graph Visualization */}
      <Card className="overflow-auto">
        <svg
          width={svgWidth}
          height={svgHeight}
          className="min-w-full"
          style={{ minHeight: svgHeight }}
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#9CA3AF" />
            </marker>
            <marker
              id="arrowhead-active"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#3B82F6" />
            </marker>
          </defs>

          {/* Edges */}
          {edges.map((edge) => {
            const sourceNode = nodes.find(n => n.id === edge.source)
            const targetNode = nodes.find(n => n.id === edge.target)

            if (!sourceNode || !targetNode) return null

            const sx = (sourceNode.position?.x || 0) + 75
            const sy = (sourceNode.position?.y || 0) + 40
            const tx = (targetNode.position?.x || 0) + 75
            const ty = (targetNode.position?.y || 0)

            // Calculate control points for curved edges
            const dx = tx - sx
            const dy = ty - sy
            const midX = sx + dx / 2
            const midY = sy + dy / 2

            // Use a bezier curve for non-vertical edges
            const isVertical = Math.abs(dx) < 50
            const path = isVertical
              ? `M ${sx} ${sy} L ${tx} ${ty - 10}`
              : `M ${sx} ${sy} Q ${midX} ${sy} ${midX} ${midY} Q ${midX} ${ty - 10} ${tx} ${ty - 10}`

            const isActive = sourceNode.status === 'completed' && targetNode.status !== 'pending'

            return (
              <g key={edge.id}>
                <path
                  d={path}
                  fill="none"
                  stroke={isActive ? '#3B82F6' : '#D1D5DB'}
                  strokeWidth={isActive ? 2 : 1.5}
                  markerEnd={`url(#arrowhead${isActive ? '-active' : ''})`}
                />
                {edge.condition && (
                  <text
                    x={midX}
                    y={midY - 5}
                    fill="#6B7280"
                    fontSize="10"
                    textAnchor="middle"
                    className="pointer-events-none"
                  >
                    {edge.condition}
                  </text>
                )}
              </g>
            )
          })}

          {/* Nodes */}
          {nodes.map((node) => {
            const x = node.position?.x || 0
            const y = node.position?.y || 0
            const nodeStatus = normalizeStatus(node.status)
            const colors = STATUS_COLORS[nodeStatus]
            const Icon = NODE_ICONS[node.type] || CpuChipIcon
            const isSelected = selectedNode === node.id

            return (
              <g
                key={node.id}
                transform={`translate(${x}, ${y})`}
                onClick={() => handleNodeClick(node.id)}
                className="cursor-pointer"
              >
                {/* Node background */}
                <rect
                  x="0"
                  y="0"
                  width="150"
                  height={compact ? '40' : '60'}
                  rx="8"
                  fill="white"
                  stroke={isSelected ? '#3B82F6' : colors.border.replace('border-', '')}
                  strokeWidth={isSelected ? 2 : 1}
                  className={`transition-all duration-200 ${colors.bg.replace('bg-', 'fill-')}`}
                  style={{ fill: colors.bg === 'bg-gray-50' ? '#F9FAFB' :
                           colors.bg === 'bg-blue-500/10' ? '#EFF6FF' :
                           colors.bg === 'bg-green-500/10' ? '#F0FDF4' :
                           colors.bg === 'bg-red-500/10' ? '#FEF2F2' : '#F9FAFB' }}
                />

                {/* Icon */}
                <foreignObject x="8" y={compact ? '8' : '12'} width="24" height="24">
                  <Icon className={`w-6 h-6 ${colors.text}`} />
                </foreignObject>

                {/* Label */}
                <text
                  x="40"
                  y={compact ? '22' : '24'}
                  fill="#111827"
                  fontSize="12"
                  fontWeight="500"
                  className="pointer-events-none"
                >
                  {node.label.length > 14 ? `${node.label.slice(0, 14)}...` : node.label}
                </text>

                {/* Status icon */}
                {nodeStatus !== 'pending' && (
                  <foreignObject x="126" y={compact ? '8' : '12'} width="20" height="20">
                    {getStatusIcon(nodeStatus)}
                  </foreignObject>
                )}

                {/* Description (if not compact) */}
                {!compact && node.description && (
                  <text
                    x="8"
                    y="45"
                    fill="#6B7280"
                    fontSize="10"
                    className="pointer-events-none"
                  >
                    {node.description.length > 22 ? `${node.description.slice(0, 22)}...` : node.description}
                  </text>
                )}

                {/* Metrics badge */}
                {node.metrics && (
                  <foreignObject x="8" y={compact ? '28' : '48'} width="140" height="16">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <span>{node.metrics.tokens.toLocaleString()} tokens</span>
                      {node.metrics.duration_ms > 0 && (
                        <>
                          <span>|</span>
                          <span>{node.metrics.duration_ms.toFixed(0)}ms</span>
                        </>
                      )}
                    </div>
                  </foreignObject>
                )}
              </g>
            )
          })}
        </svg>
      </Card>

      {/* Node Details Panel */}
      {selectedNode && !compact && (
        <Card>
          {(() => {
            const node = nodes.find(n => n.id === selectedNode)
            if (!node) return null

            const nodeStatus = normalizeStatus(node.status)
            const colors = STATUS_COLORS[nodeStatus]
            const Icon = NODE_ICONS[node.type] || CpuChipIcon

            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{node.label}</h4>
                      <p className="text-xs text-gray-500 capitalize">{node.type} node</p>
                    </div>
                  </div>
                  <Badge
                    variant={
                      nodeStatus === 'completed' ? 'success' :
                      nodeStatus === 'error' ? 'error' :
                      nodeStatus === 'running' ? 'warning' : 'gray'
                    }
                  >
                    {nodeStatus}
                  </Badge>
                </div>

                {node.description && (
                  <p className="text-sm text-gray-600">{node.description}</p>
                )}

                {node.metrics && (
                  <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-200">
                    <div>
                      <p className="text-xs text-gray-500">Tokens</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {node.metrics.tokens.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {node.metrics.duration_ms.toFixed(0)}ms
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Cost</p>
                      <p className="text-sm font-semibold text-gray-900">
                        ${node.metrics.cost.toFixed(4)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
        </Card>
      )}

      {/* Graph Metadata */}
      {!compact && graphData.metadata && (
        <Card>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Configuration</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            {'quality_threshold' in graphData.metadata && (
              <div>
                <p className="text-xs text-gray-500">Quality Threshold</p>
                <p className="font-medium text-gray-900">{graphData.metadata.quality_threshold}</p>
              </div>
            )}
            {'max_revisions' in graphData.metadata && (
              <div>
                <p className="text-xs text-gray-500">Max Revisions</p>
                <p className="font-medium text-gray-900">{graphData.metadata.max_revisions}</p>
              </div>
            )}
            {'memory_layers' in graphData.metadata && (
              <div>
                <p className="text-xs text-gray-500">Memory Layers</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {graphData.metadata.memory_layers.map((layer: string) => (
                    <Badge key={layer} variant="gray" size="sm">{layer}</Badge>
                  ))}
                </div>
              </div>
            )}
            {'agent_count' in graphData.metadata && (
              <div>
                <p className="text-xs text-gray-500">Agents</p>
                <p className="font-medium text-gray-900">{graphData.metadata.agent_count}</p>
              </div>
            )}
            {'domain' in graphData.metadata && (
              <div>
                <p className="text-xs text-gray-500">Domain</p>
                <Badge variant="primary" size="sm">{graphData.metadata.domain}</Badge>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
})

export default LangGraphVisualizer
